<template>
  <b-row align-v="start" no-gutters>
    <b-col v-if="item.product.image" cols="auto">
      <b-img alt="" class="img-border product-image" :src="getImage(item.product.image.original)" />
    </b-col>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <b-col class="ml-2">
      {{ item.product.name }}
      <div v-if="item.product.description" v-html="item.product.description"></div>

      <b-button
        v-if="billingIntervalOptions"
        v-testid="'membership-billing-toggle'"
        class="m-0 p-0 text-left d-block billing-interval-toggle"
        variant="link"
        :aria-busy="switching ? 'true' : 'false'"
        :aria-describedby="switchError ? `${switchErrorId}` : null"
        @click="onToggleBillingInterval"
      >
        {{ toggleLabel }}
        <span v-if="switching" role="status" class="fa-pulse d-inline-block ml-1">
          <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
          <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
        </span>
      </b-button>

      <p v-if="switchError" :id="switchErrorId" role="alert" class="text-danger mt-1 mb-0">
        {{ $t('membership.switchError') }}
      </p>

      <span class="sr-only" aria-live="polite">{{ switching ? '' : billingIntervalAnnouncement }}</span>
    </b-col>
    <b-col>
      <h3 v-testid="'membership-line-price'" class="my-0 text-right">
        <span :style="{ textDecoration: Number(item.subtotal) !== Number(item.subtotal - item.discount) ? 'line-through' : '' }">
          {{ $n(item.subtotal, 'currency') }}
        </span>
        <span v-if="Number(item.subtotal) !== Number(item.subtotal - item.discount)" class="ml-1">
          <span v-if="Number(item.subtotal - item.discount) > 0">{{ $n(item.subtotal - item.discount, 'currency') }}</span>
          <span v-else>{{ $t('components.totals.free') }}</span>
        </span>
      </h3>
      <b-button block class="m-0 p-0 text-right" variant="link" @click="$emit('remove-item', item.id)">{{ $t('dp.generic.remove') }}</b-button>
    </b-col>

    <b-col v-if="requiresAcknowledgement" cols="12">
      <label class="mt-2 w-100 d-flex align-items-start commitment-ack-panel" :for="acknowledgementId">
        <input
          :id="acknowledgementId"
          v-testid="'membership-commitment-ack'"
          type="checkbox"
          class="mt-1 mr-2 flex-shrink-0"
          :checked="acknowledged"
          :aria-invalid="acknowledgementError ? 'true' : 'false'"
          :aria-describedby="acknowledgementError ? `${acknowledgementId}-error` : null"
          @change="onAcknowledgementChanged($event)"
        />
        <span class="d-flex flex-column flex-grow-1 commitment-ack-text">
          <span>{{ $t('membership.monthly_commitment_ack') }}</span>
        </span>
      </label>
      <!-- Outside the <label>: nested here it would be folded into the checkbox's accessible
           name, and aria-describedby would then point at a node inside that name. -->
      <span
        v-if="acknowledgementError"
        :id="`${acknowledgementId}-error`"
        v-testid="'membership-commitment-error'"
        role="alert"
        class="text-danger d-block"
      >
        {{ $t('membership.monthly_commitment_ack_error') }}
      </span>
    </b-col>
  </b-row>
</template>

<script>
import { getImage } from '@/utils';
import { MEMBERSHIP_BILLING_INTERVAL } from '@/api/services/billingIntervalOptions';

export default {
  name: 'MembershipSubscriptionProduct',
  props: {
    item: {
      type: Object,
      required: true,
    },
    switching: {
      type: Boolean,
      default: false,
    },
    switchError: {
      type: Boolean,
      default: false,
    },
    requiresAcknowledgement: {
      type: Boolean,
      default: false,
    },
    acknowledged: {
      type: Boolean,
      default: false,
    },
    acknowledgementError: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    billingIntervalOptions() {
      return this.item.billingIntervalOptions;
    },
    toggleLabel() {
      const options = this.billingIntervalOptions;
      if (!options) return '';

      const price = this.formatCurrency(options.targetPrice);

      if (options.targetInterval === MEMBERSHIP_BILLING_INTERVAL.MONTHLY) {
        return this.$t('membership.switchToMonthly', { price });
      }

      if (options.annualSaving <= 0) {
        return this.$t('membership.switchToAnnualNoSaving', { price });
      }

      return this.$t('membership.switchToAnnual', { saving: this.formatCurrency(options.annualSaving) });
    },
    acknowledgementId() {
      return `membership-commitment-ack-${this.item.id}`;
    },
    switchErrorId() {
      return `membership-switch-error-${this.item.id}`;
    },
    // Announced politely after a switch settles: the toggle re-renders with the opposite label
    // and the price changes, neither of which is conveyed to assistive tech on its own.
    billingIntervalAnnouncement() {
      const options = this.billingIntervalOptions;
      if (!options) return '';

      const key =
        options.currentInterval === MEMBERSHIP_BILLING_INTERVAL.MONTHLY ? 'membership.nowBilledMonthly' : 'membership.nowBilledAnnually';

      return this.$t(key, { price: this.formatCurrency(this.item.subtotal - this.item.discount) });
    },
  },
  methods: {
    getImage,
    formatCurrency(value) {
      return value === null || value === undefined ? '' : this.$n(value, 'currency');
    },
    onToggleBillingInterval() {
      const options = this.billingIntervalOptions;
      if (!options || this.switching) return;

      this.$emit('change-billing-interval', { itemId: this.item.id, billingInterval: options.targetInterval });
    },
    onAcknowledgementChanged(event) {
      this.$emit('acknowledgement-changed', { itemId: this.item.id, acknowledged: Boolean(event.target.checked) });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@docupet/component-library/src/assets/styles/_variables';

.billing-interval-toggle {
  white-space: nowrap;
}

.commitment-ack-panel {
  background-color: $dp-gray-1;
  border: 1px solid $dp-gray-9;
  border-radius: 0.25rem;
  padding: 0.75rem;
  cursor: pointer;
  margin-bottom: 0;
}

.commitment-ack-text {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
